import { Injectable } from '@angular/core';
import { lastValueFrom } from "rxjs";
import { ViewModelLineType, ViewModelLine, isCalculationLineApi } from "../../../../state-management/calculation-state";
import { CalculationMetaApi } from "../../../../types";
import { PDFDocument, PDFFont, PDFPage, RGB, rgb } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { TERMS_AND_CONDITIONS } from '../../../../constants/pdf-export';
import { FontWeight, PAPER_DIMENSIONS, ROW_STYLES } from '../../pdf-export-service.settings';
import { SvgPath } from '../../types/svg-types';
import { Dimensions, Position } from '../../types/pdf-measurement.types';
import { TO_BUILD_LOGO_SVG_PATHS, TO_BUILD_PAYOFF_SVG_PATHS, TO_BUILD_PROJECTS_LOGO_SVG_PATHS, TO_BUILD_PROJECTS_PAYOFF_SVG_PATHS } from '../../images/logo-svg';
import { toNumberOrZero, TRANSLATE_CALCULATION_PATH } from '../../data-extraction';
import { Branding, CellFormat, CellSide, FontStyle, TextStyle } from '../../types/pdf-cell.types';
import { ColumnProps, PdfExportLine, PdfExportOptions, PdfExportParagraph, PdfExportParagraphFonts, PdfExportSpan } from '../../types/pdf-export.types';
import { additionalWhitespace, BULLET_WIDTH, DEFAULT_CELL_MARGINS, DEFAULT_HEADER_STYLE, DEFAULT_ROW_STYLE, FOOTER_HEIGHT, FOOTER_MAP, formatterPercentage, PageNumberText1, PageNumberText2, PDF_LINE_SPACING_FACTOR, TRANSLATIONS } from '../../pdf-export.constants';
import { formatterCurrency, formatterDecimal, formatterInteger } from '../../pdf-formatters';
import { CalculationTableBuilder } from '../../types/calculation-table-builder.types';
import { CalculationColumn } from '../../types/column.types';


const styleToFont = (style: TextStyle) => {
    return fontFormat(style.weight, style.isItalic ? FontStyle.ITALIC : undefined, style.isUnderlined ? FontStyle.UNDERLINED : undefined)
}

const fontFormat = (weight: FontWeight = FontWeight.REGULAR, ...styles: (FontStyle | undefined)[]): number => {
    // Combineer alle stijlen door gebruik te maken van bitwise OR
    const combinedStyles = styles.reduce((acc, style) => acc ? acc : (style ?? 0), 0) ?? 0;

    // Return een combinatie van gewicht en gecombineerde stijlen
    return weight | combinedStyles;
}

// TODO: move to configuration

@Injectable({
    providedIn: 'root'
})
export class PdfExportService {
    private readonly rowStyles: Partial<Record<ViewModelLineType, Partial<Record<CalculationColumn, CellFormat>>>> = {};

    constructor(private readonly http: HttpClient) { }

    public async createPdf(viewModelLines: ViewModelLine[], meta: CalculationMetaApi, options: PdfExportOptions): Promise<Blob> {
        // This is hacky, but it works for now. Decided to not use an extra section for vat lines
        // But a hidden property on the lines for the UI to filter out the vat lines
        // So, this is where it comes together now. But should be refactored later on
        const lines = options.includeVat ? viewModelLines : viewModelLines.filter(line => !line.hidden);

        const timestamp = new Date().getTime();
        // console.log('PDF', options);
        const fontsDirectoryPath = '../../assets/fonts/roboto';
        const robotoBoldUrl = `${fontsDirectoryPath}/roboto-bold-v1.ttf?v=${timestamp}`;
        const robotoMediumUrl = `${fontsDirectoryPath}/roboto-medium-v1.ttf?v=${timestamp}`;
        const robotoRegularUrl = `${fontsDirectoryPath}/roboto-regular-v1.ttf?v=${timestamp}`;
        const robotoLightUrl = `${fontsDirectoryPath}/roboto-light-v1.ttf?v=${timestamp}`;
        const robotoThinUrl = `${fontsDirectoryPath}/roboto-thin-v1.ttf?v=${timestamp}`;
        const robotoItalicUrl = `${fontsDirectoryPath}/roboto-thinitalic-v1.ttf?v=${timestamp}`;

        const headers = new HttpHeaders({
            'Cache-Control': 'no-cache',  // Voorkom caching van deze bestanden
        });

        const robotoBoldBytes = await lastValueFrom(this.http.get(robotoBoldUrl, { headers, responseType: 'arraybuffer' }));
        const robotoMediumBytes = await lastValueFrom(this.http.get(robotoMediumUrl, { headers, responseType: 'arraybuffer' }));
        const robotoRegularBytes = await lastValueFrom(this.http.get(robotoRegularUrl, { headers, responseType: 'arraybuffer' }));
        const robotoLightBytes = await lastValueFrom(this.http.get(robotoLightUrl, { headers, responseType: 'arraybuffer' }));
        const robotoThinBytes = await lastValueFrom(this.http.get(robotoThinUrl, { headers, responseType: 'arraybuffer' }));
        const robotoThinItalicBytes = await lastValueFrom(this.http.get(robotoItalicUrl, { headers, responseType: 'arraybuffer' }));

        const pdfDoc = await PDFDocument.create();

        // Register fontkit
        pdfDoc.registerFontkit(fontkit);

        // Embed the Roboto fonts
        const
            [
                robotoBoldFont,
                robotoMediumFont,
                robotoRegularFont,
                robotoLightFont,
                robotoThinFont,
                robotoThinItalicFont,
            ] = await Promise.all([
                pdfDoc.embedFont(robotoBoldBytes),
                pdfDoc.embedFont(robotoMediumBytes),
                pdfDoc.embedFont(robotoRegularBytes),
                pdfDoc.embedFont(robotoLightBytes),
                pdfDoc.embedFont(robotoThinBytes),
                pdfDoc.embedFont(robotoThinItalicBytes)
            ])

        const fontMapDefault: PdfExportParagraphFonts = {
            [fontFormat(FontWeight.BOLD)]: robotoBoldFont,
            [fontFormat(FontWeight.MEDIUM)]: robotoMediumFont,
            [fontFormat(FontWeight.REGULAR)]: robotoRegularFont,
            [fontFormat(FontWeight.LIGHT)]: robotoLightFont,
            [fontFormat(FontWeight.THIN)]: robotoThinFont,
            [fontFormat(FontWeight.BOLD, FontStyle.UNDERLINED)]: robotoBoldFont,
            [fontFormat(FontWeight.MEDIUM, FontStyle.UNDERLINED)]: robotoMediumFont,
            [fontFormat(FontWeight.REGULAR, FontStyle.UNDERLINED)]: robotoRegularFont,
            [fontFormat(FontWeight.LIGHT, FontStyle.UNDERLINED)]: robotoLightFont,
            [fontFormat(FontWeight.THIN, FontStyle.UNDERLINED)]: robotoThinFont,
            [fontFormat(FontWeight.BOLD, FontStyle.ITALIC)]: robotoThinItalicFont,
            [fontFormat(FontWeight.MEDIUM, FontStyle.ITALIC)]: robotoThinItalicFont,
            [fontFormat(FontWeight.REGULAR, FontStyle.ITALIC)]: robotoThinItalicFont,
            [fontFormat(FontWeight.LIGHT, FontStyle.ITALIC)]: robotoThinItalicFont,
            [fontFormat(FontWeight.THIN, FontStyle.ITALIC)]: robotoThinItalicFont,
            [fontFormat(FontWeight.BOLD, FontStyle.ITALIC, FontStyle.UNDERLINED)]: robotoThinItalicFont,
            [fontFormat(FontWeight.MEDIUM, FontStyle.ITALIC, FontStyle.UNDERLINED)]: robotoThinItalicFont,
            [fontFormat(FontWeight.REGULAR, FontStyle.ITALIC, FontStyle.UNDERLINED)]: robotoThinItalicFont,
            [fontFormat(FontWeight.LIGHT, FontStyle.ITALIC, FontStyle.UNDERLINED)]: robotoThinItalicFont,
            [fontFormat(FontWeight.THIN, FontStyle.ITALIC, FontStyle.UNDERLINED)]: robotoThinItalicFont,
        }

        // Create front page
        this.addFrontPage(pdfDoc, meta, options, fontMapDefault);

        // Add calculation pages
        // this.addCalculationPages(lines, pdfDoc, meta, options, fontMapDefault);
        this.addCalculationPages2(lines, pdfDoc, meta, options, fontMapDefault);

        // Add terms and conditions
        if (options.includeTerms) {
            this.addGeneralTermsAndConditions(pdfDoc, meta, options, fontMapDefault);
        }

        // Add page numbers
        this.addNumbersToPages(pdfDoc, meta, options, fontMapDefault);

        // Add footers
        this.addFooterToPages(pdfDoc, options, fontMapDefault);

        // Serialiseer het PDF-document in een Uint8Array
        const pdfBytes = await pdfDoc.save();

        // Converteer de Uint8Array naar een Blob
        const blob = new Blob([pdfBytes as any], { type: 'application/pdf' });

        return blob;
    }

    private addFrontPage(doc: PDFDocument, meta: CalculationMetaApi, options: PdfExportOptions, fonts: PdfExportParagraphFonts) {
        let logoSvgPaths: SvgPath[] = [];

        switch (options.branding) {
            case Branding.TO_BUILD:
                logoSvgPaths = TO_BUILD_LOGO_SVG_PATHS;
                break;
            case Branding.TO_BUILD_PROJECTS:
                logoSvgPaths = TO_BUILD_PROJECTS_LOGO_SVG_PATHS
                break;
            case Branding.PGA:
                break;
        }

        const logoDimensions: Dimensions = { width: 150, height: 20 };
        const pageSize: Dimensions = { width: this.pageWidth(options), height: this.pageHeight(options) }
        // Add the front page
        const page = doc.addPage([pageSize.width, pageSize.height]);

        const relX = pageSize.width / 10 * 3;
        let relY = pageSize.height / 4 * 3;

        // Add logo
        this.drawSvg(page,
            logoSvgPaths,
            {
                x: (pageSize.width) - (options.margins.right ?? 0) - (logoDimensions.width * 1.4),
                y: (pageSize.height / 20 * 19) - (options.margins.top ?? 0)
            });

        // Draw title
        page.drawText(options.title[options.language ?? 'nl'] ?? '', {
            x: relX,
            y: relY,
            size: 26,
            font: fonts[fontFormat(FontWeight.THIN)],
            color: rgb(0, 0, 0),
        });

        relY -= 13;

        // Draw project information
        const projectInfoParagraph = this.convertTextToParagraph(`${meta.projectId} ${meta.description}`, pageSize.width / 2, {
            textStyle: {
                fontSize: 14,
                weight: FontWeight.LIGHT,
                color: 0x000000
            },
            margin: {
                top: 0,
                bottom: 10
            },
            horizontalTextAlignment: 'left'
        }, fonts);

        this.drawParagraphOnPage(page, projectInfoParagraph, { x: relX, y: relY }, fonts);

        relY -= projectInfoParagraph.paragraphDimensions.height;

        // Draw project address
        const addressText = [meta.address?.address, meta.address?.postalCode, meta.address?.city, meta.address?.country].filter(f => !!f).join(' | ');

        if (addressText.length > 0) {
            const addressParagraph = this.convertTextToParagraph(addressText, pageSize.width / 2, {
                textStyle: {
                    fontSize: 11,
                    weight: FontWeight.THIN,
                    color: 0x000000
                },
                margin: {
                    top: 0,
                    bottom: 5
                },
                horizontalTextAlignment: 'left'
            }, fonts);


            this.drawParagraphOnPage(page, addressParagraph, { x: relX, y: relY }, fonts);

            relY -= addressParagraph.paragraphDimensions.height;
        }

        // Draw project client
        if (meta.client) {
            const clientParagraph = this.convertTextToParagraph(`Opdrachtgever: ${meta.client}`, pageSize.width / 2, {
                textStyle: {
                    fontSize: 11,
                    weight: FontWeight.THIN,
                    color: 0x000000
                },
                margin: {
                    top: 0,
                    bottom: 5
                },
                horizontalTextAlignment: 'left'
            }, fonts);


            this.drawParagraphOnPage(page, clientParagraph, { x: relX, y: relY }, fonts);

            relY -= clientParagraph.paragraphDimensions.height;
        }

        // Draw version
        const versionString = `Versie: v${meta.displayVersion || meta.currentVersion}.0 - ${this.formatDateToDutch(new Date(meta.displayDate || meta.versionDate || new Date().valueOf()))}`;
        const versionParagraph = this.convertTextToParagraph(versionString, pageSize.width / 2, {
            textStyle: {
                fontSize: 11,
                weight: FontWeight.REGULAR,
                color: 0x000000
            },
            margin: {
                top: 0,
                bottom: 5
            },
            horizontalTextAlignment: 'left'
        }, fonts);


        this.drawParagraphOnPage(page, versionParagraph, { x: relX, y: relY }, fonts);

        relY -= versionParagraph.paragraphDimensions.height;
    }

    private addGeneralTermsAndConditions(doc: PDFDocument, meta: CalculationMetaApi, options: PdfExportOptions, fonts: PdfExportParagraphFonts) {
        const COLUMN_TARGET_WIDTH = 280;
        const INDENT_WIDTH = 15;
        const HEADER_HEIGHT = 60;
        const FOOTER_HEIGHT = 70;
        const COLUMN_GAP = 5;
        const BODY_FONT_SIZE = 7.7;
        const HEADER_1_FONT_SIZE = 11;
        const HEADER_2_FONT_SIZE = 9;

        const {
            language = 'nl',
            margins: {
                left: marginLeft = 0,
                top: marginTop = 0,
                right: marginRight = 0,
                bottom: marginBottom = 0
            } = { left: 0, top: 0, right: 0, bottom: 0 }
        } = options;

        const pageSize: Dimensions = { width: this.pageWidth(options), height: this.pageHeight(options) };

        // Calculate columns, the terms and conditions are displayed in multiple columns
        const canvasWidth = (pageSize.width - marginLeft - marginRight);
        const nrOfColumns = canvasWidth / COLUMN_TARGET_WIDTH;
        const nrOfColumnsRound = Math.max(1, Math.ceil(nrOfColumns));
        const columnWidth = (canvasWidth - nrOfColumnsRound * COLUMN_GAP) / nrOfColumnsRound;

        // This will hold the current page
        let page: PDFPage = this.createTermsPage(doc, pageSize, options);

        // Draw text
        let curColumn = 0;
        let relY = pageSize.height - marginTop - HEADER_HEIGHT;


        // Print title
        if (TERMS_AND_CONDITIONS.title) {
            const mainTitle = this.convertTextToParagraph(TERMS_AND_CONDITIONS.title[language] ?? '', columnWidth, {
                textStyle: {
                    fontSize: HEADER_1_FONT_SIZE,
                    weight: FontWeight.BOLD,
                    color: 0x606060
                },
                margin: {
                    top: 0,
                    bottom: 6
                },
                horizontalTextAlignment: 'center'
            }, fonts);

            this.drawParagraphOnPage(page, mainTitle, { x: marginLeft + curColumn * (columnWidth + COLUMN_GAP), y: relY }, fonts);
            relY -= mainTitle.paragraphDimensions.height;
        }

        for (const paragraph of TERMS_AND_CONDITIONS.paragraphs) {
            // Reset x pos
            let relX = marginLeft + curColumn * (columnWidth + COLUMN_GAP);

            // console.log('PARAGRAPH', paragraph.text[language]);
            const paragraphTitle = paragraph.title ? this.convertTextToParagraph(paragraph.title[language] ?? '', columnWidth, {
                textStyle: {
                    fontSize: HEADER_2_FONT_SIZE,
                    weight: FontWeight.BOLD,
                    color: 0x606060
                },
                margin: {
                    top: 6,
                    bottom: 3
                }
            }, fonts) :
                undefined;

            // Convert nested text to extra columns
            const textWidth = columnWidth - (paragraph.indents?.length ?? 0) * INDENT_WIDTH;
            const renderedParagraph = this.convertTextToParagraph(paragraph.text?.[language] ?? '', textWidth, {
                margin: {
                    right: 6,
                    bottom: 2
                },
                textStyle: {
                    fontSize: BODY_FONT_SIZE,
                    weight: FontWeight.LIGHT
                },
                lineSpacing: 1.1
            }, fonts);

            // Check if paragraph fits on page and in current column
            if (page === undefined || relY - (renderedParagraph.paragraphDimensions.height + (paragraphTitle?.paragraphDimensions?.height ?? 0)) < (marginBottom + FOOTER_HEIGHT)) {
                // New column?
                curColumn++;

                // New page?
                if (page === undefined || curColumn >= nrOfColumnsRound) {
                    page = this.createTermsPage(doc, pageSize, options);
                    curColumn = 0;
                }

                relX = marginLeft + curColumn * (columnWidth + COLUMN_GAP);
                relY = pageSize.height - marginTop - HEADER_HEIGHT;

            }

            // Render title
            if (paragraphTitle) {
                this.drawParagraphOnPage(page, paragraphTitle, { x: relX, y: relY }, fonts);
                relY -= paragraphTitle.paragraphDimensions.height;
            }

            // console.log(renderedParagraph);

            for (const indent of paragraph.indents ?? []) {
                const indentParagraph = this.convertTextToParagraph(indent, INDENT_WIDTH, {
                    horizontalTextAlignment: 'right',
                    margin: {
                        right: 5
                    },
                    textStyle: {
                        fontSize: BODY_FONT_SIZE,
                        weight: FontWeight.REGULAR
                    }
                }, fonts);

                // console.log('INDENT', indentParagraph);

                this.drawParagraphOnPage(page, indentParagraph, { x: relX, y: relY }, fonts);

                relX += INDENT_WIDTH;
            }

            this.drawParagraphOnPage(page, renderedParagraph, { x: relX, y: relY }, fonts);

            relY -= renderedParagraph.paragraphDimensions.height;
        }
    }

    private createTermsPage(doc: PDFDocument, pageSize: Dimensions, options: PdfExportOptions) {
        let logoSvgPaths: SvgPath[] = [];

        switch (options.branding) {
            case Branding.TO_BUILD:
                logoSvgPaths = TO_BUILD_LOGO_SVG_PATHS;
                break;
            case Branding.TO_BUILD_PROJECTS:
                logoSvgPaths = TO_BUILD_PROJECTS_LOGO_SVG_PATHS
                break;
            case Branding.PGA:
                break;
        }

        const page = doc.addPage([pageSize.width, pageSize.height]);

        if (options.branding !== Branding.TO_BUILD_PROJECTS) {
            // Add logo
            this.drawSvg(page,
                logoSvgPaths.map(m => ({ ...m, scale: 1 })),
                {
                    x: (pageSize.width) - (options.margins.right ?? 0) - 200,
                    y: (pageSize.height - (options.margins.top ?? 0)) - (options.margins.top ?? 0)
                });
        }


        return page;
    }

    private addCalculationPages2(lines: ViewModelLine[], doc: PDFDocument, meta: CalculationMetaApi, options: PdfExportOptions, fonts: PdfExportParagraphFonts) {
        // Calculate size for calculation table on every page
        const pageSize: Dimensions = { width: this.pageWidth(options), height: this.pageHeight(options) };
        const maxTableWidth = pageSize.width - (options.margins.left ?? 0) - (options.margins.right ?? 0);

        // Save page header for reuse
        const pageHeader = this.createCalculationPageHeader(meta, maxTableWidth, fonts);
        const tableHeader = this.createCalculationTableHeaderParagraph(options, maxTableWidth, fonts);
        let tableHeaderHeight = tableHeader.reduce<number>((p, c) => Math.max(p, c.paragraphDimensions.height), 0);
        const totalHeaderHeight = pageHeader.paragraphDimensions.height + tableHeaderHeight;

        const maxCalculationHeight = pageSize.height - (options.margins.top ?? 0) - (options.margins.bottom ?? 0) - totalHeaderHeight - FOOTER_HEIGHT;

        const pages = this.calculationParagraphs(lines, meta, options, fonts, maxCalculationHeight);
        let currentPage: PDFPage;

        for (const page of pages) {
            currentPage = doc.addPage([pageSize.width, pageSize.height]);

            let vertOffset = pageSize.height - (options.margins.top ?? 0);;

            // Draw header
            this.drawParagraphOnPage(currentPage, pageHeader, { x: (options.margins.left ?? 0), y: vertOffset }, fonts);
            vertOffset -= pageHeader.paragraphDimensions.height;

            // Draw table header
            this.drawCalculationTableHeader(currentPage, { x: (options.margins.left ?? 0), y: vertOffset }, tableHeader, fonts);
            vertOffset -= tableHeaderHeight;

            for (const row of page) {
                // Reset x position
                let horOffset = (options.margins.left ?? 0);
                for (const cell of row.cells) {
                    this.drawParagraphOnPage(currentPage, cell, { x: horOffset, y: vertOffset }, fonts);
                    horOffset += cell.paragraphDimensions.width;
                }
                vertOffset -=( row.dimensions?.height ?? 0) + (row.margins?.top ?? 0) + (row.margins?.bottom ?? 0);
            }
        }
    }

    createCalculationTableHeaderParagraph(options: PdfExportOptions, maxTableWidth: number, fonts: PdfExportParagraphFonts) {
        // Calculate size for calculation table on every page
        const allVisibleColumns = this.calculationColumns(options.columns, maxTableWidth);

        // Save column headers for reuse
        const headerParagraph = allVisibleColumns
            .map((column) => this.convertTextToParagraph(
                column.columnHeaderLabel?.[options.language ?? 'nl'] ?? '',
                column.calculatedWith ?? 0,
                {
                    ...DEFAULT_HEADER_STYLE,
                    horizontalTextAlignment: column.horizontalTextAlignment,
                    margin: {
                        ...DEFAULT_HEADER_STYLE.margin,
                        right: column.margin?.right,
                        left: column.margin?.left
                    }
                },
                fonts
            ));

        return headerParagraph;
    }

    private getRowStyle(lineType: ViewModelLineType, column: ColumnProps): CellFormat {
        const { columnId, margin: columnMargin, colType, horizontalTextAlignment } = column;

        // Try to get from mapping
        const calculatedStyle = this.rowStyles[lineType]?.[columnId];

        if (calculatedStyle) {
            return calculatedStyle;
        }

        const rowStyle = ROW_STYLES[lineType];
        const columnOverride = rowStyle.columnFormatOverrides?.[columnId];

        const defaultHorizontalTextAlignment =
            ['currency', 'number', 'integer', 'percentage'].includes(colType ?? '')
                ? 'right'
                : horizontalTextAlignment;

        const cellFormat: CellFormat = {
            lineSpacing:
                columnOverride?.lineSpacing
                ?? rowStyle.lineSpacing
                ?? PDF_LINE_SPACING_FACTOR,

            horizontalTextAlignment:
                columnOverride?.horizontalTextAlignment
                ?? defaultHorizontalTextAlignment,

            backgroundColor:
                columnOverride?.backgroundColor
                ?? rowStyle.backgroundColor,

            margin: {
                ...DEFAULT_CELL_MARGINS,
                ...rowStyle.margin,
                ...columnMargin,
                ...columnOverride?.margin,
            },

            textStyle: {
                ...DEFAULT_ROW_STYLE.textStyle!,
                ...rowStyle.textStyle,
                ...columnOverride?.textStyle,
            },

            border: {
                ...DEFAULT_ROW_STYLE.border,
                ...rowStyle.border,
                ...columnOverride?.border,
            },
        };

        // Save calculated style
        if (!this.rowStyles[lineType]) {
            this.rowStyles[lineType] = {} as Record<CalculationColumn, CellFormat>;
        }
        this.rowStyles[lineType][columnId] = cellFormat;

        return cellFormat;
    }

    private calculationParagraphs(
        lines: ViewModelLine[],
        meta: CalculationMetaApi,
        options: PdfExportOptions,
        fonts: PdfExportParagraphFonts,
        canvasHeight: number
    ) {
        // Calculate size for calculation table on every page
        const pageSize: Dimensions = { width: this.pageWidth(options), height: this.pageHeight(options) };
        const horizontalMargins = (options.margins.left ?? 0) + (options.margins.right ?? 0);
        const maxTableWidth = pageSize.width - horizontalMargins;
        const allVisibleColumns = this.calculationColumns(options.columns, maxTableWidth);

        // Exception for text lines without quantity, these should use all available space
        const descriptionColumnIndex = allVisibleColumns.findIndex(c => c.columnId === 'DESCRIPTION');
        const adjustedTextLineColumns = [
            ...allVisibleColumns.slice(0, descriptionColumnIndex), // Include description column
            {
                ...allVisibleColumns[descriptionColumnIndex],   // Keep all column left of the description column the same
                calculatedWith: allVisibleColumns.slice(descriptionColumnIndex, allVisibleColumns.length).reduce((p, c) => p + (c.calculatedWith ?? 0), 0) // Use all space for description column, except for the last column
            },
            ...allVisibleColumns.slice(descriptionColumnIndex + 1).map(c => ({ ...c, calculatedWith: 0 })) // Exclude description column
        ]

        // User builder class to build the calculation table and add page breaks
        const tableBuilder = new CalculationTableBuilder(meta, options, canvasHeight);

        let lastLineType: ViewModelLineType | undefined = undefined;

        // First run will create paragraphs for every line and add them to the table builder
        for (const vmLine of lines) {
            // Only include tail if necessary
            if (!options.includeTail && vmLine.section === 'tail') {
                continue;
            }

            // Get the line type
            const { type: lineType } = vmLine;

            // Check if this is a text line without quantity. If so, the description column should use all available space
            const isTextLineWithoutQuantity = isCalculationLineApi(vmLine.line)
                && (lineType === ViewModelLineType.TEXT)
                && (!vmLine.line.quantity && !vmLine.line.unit)

            const useColumns = isTextLineWithoutQuantity ? adjustedTextLineColumns : allVisibleColumns;

            const cells = useColumns
                .map<PdfExportParagraph>(column => {
                    const cellFormat = this.getRowStyle(lineType, column);
                    const extractData = this.extractAndFormatData(vmLine, column, options, meta);

                    const paragraph = this.convertTextToParagraph(
                        extractData,
                        column.calculatedWith ?? 0,
                        cellFormat,
                        fonts
                    );

                    return paragraph;
                });

            // Add rows
            tableBuilder.addRow(cells, lineType, {
                bottom: additionalWhitespace[lineType] ?? 0
            });
        }

        return tableBuilder.build();
    }

    private extractAndFormatData(line: ViewModelLine, columnProps: ColumnProps, options: PdfExportOptions, meta: CalculationMetaApi): string {
        // console.log('EXTRACT DATA', line, columnProps);
        switch (columnProps.colType) {
            case undefined:
            case 'html':
            default:
                const textValue = !columnProps.dataType || columnProps.dataType === 'variable' ? TRANSLATE_CALCULATION_PATH[columnProps.columnId](line, meta) :
                    columnProps.fixedValue?.[options.language ?? 'nl'] ?? '';
                return textValue.toString();
            case 'number':
                const numberValue = !columnProps.dataType || columnProps.dataType === 'variable' ? TRANSLATE_CALCULATION_PATH[columnProps.columnId](line, meta) :
                    columnProps.fixedValue?.[options.language ?? 'nl'] ?? '';

                // Make sure it is a number
                let toNum = Number(numberValue);

                if (isNaN(toNum)) {
                    toNum = 0;
                }

                if (!toNum) { // Also hides zero values
                    return '';
                } else if (Number.isInteger(toNum)) {
                    return formatterInteger.format(toNum); // Return integers values without decimals
                } else {
                    // Display with minimum of two decimals
                    return formatterDecimal.format(toNum);
                }
            case 'currency':
                const currencyValue = !columnProps.dataType || columnProps.dataType === 'variable' ? TRANSLATE_CALCULATION_PATH[columnProps.columnId](line, meta) :
                    columnProps.fixedValue?.[options.language ?? 'nl'];
                return currencyValue ? formatterCurrency.format(toNumberOrZero(currencyValue)) : '';
            case 'percentage':
                const percentageValue = !columnProps.dataType || columnProps.dataType === 'variable' ? TRANSLATE_CALCULATION_PATH[columnProps.columnId](line, meta) : 0
                return percentageValue ? formatterPercentage.format(toNumberOrZero(percentageValue)) : '';

        }
    }

    private addNumbersToPages(doc: PDFDocument, meta: CalculationMetaApi, options: PdfExportOptions, fonts: PdfExportParagraphFonts) {
        const pageWidth = this.pageWidth(options);
        // Prepare date paragraph to add to all pages
        const dateParagraph = this.convertTextToParagraph(new Date(meta.displayDate ?? meta.versionDate ?? new Date().toISOString()).toLocaleDateString(), pageWidth - options.margins!.left! - options.margins!.right!, {
            textStyle: {
                fontSize: 8,
                weight: FontWeight.LIGHT
            },
            margin: {
                top: 0,
                bottom: 1.5
            },
            horizontalTextAlignment: 'left'
        }, fonts);

        const pages = doc.getPages();
        const totalPageCount = pages.length;

        doc.getPages().forEach((page, index) => {
            if (index > 0) {
                const pageHeader = this.convertTextToParagraph(
                    `${PageNumberText1[options.language ?? 'nl']} ${index + 1} ${PageNumberText2[options.language ?? 'nl']} ${totalPageCount}`,
                    pageWidth - (options.margins.left ?? 0) - (options.margins.right ?? 0),
                    {
                        textStyle: {
                            fontSize: 8,
                            weight: FontWeight.LIGHT
                        },
                        margin: {
                            top: 0,
                            bottom: 1.5
                        },
                        horizontalTextAlignment: 'right'
                    }, fonts);

                this.drawParagraphOnPage(page, pageHeader, {
                    x: (options.margins.left ?? 0),
                    y: this.pageHeight(options) - (options.margins.top ?? 0)
                }, fonts);

                this.drawParagraphOnPage(page, dateParagraph, {
                    x: (options.margins.left ?? 0),
                    y: this.pageHeight(options) - (options.margins.top ?? 0)
                }, fonts);
            }
        });
    }

    private addFooterToPages(doc: PDFDocument, options: PdfExportOptions, fonts: PdfExportParagraphFonts) {
        let logoSvgPaths: SvgPath[] = [];

        switch (options.branding) {
            case Branding.TO_BUILD:
                logoSvgPaths = TO_BUILD_PAYOFF_SVG_PATHS;
                break;
            case Branding.TO_BUILD_PROJECTS:
                logoSvgPaths = TO_BUILD_PROJECTS_PAYOFF_SVG_PATHS
                break;
            case Branding.PGA:
                break;
        }

        const logoHorOffset = 20;
        const logoDimensions: Dimensions = { width: 155, height: 60 };

        // Get text in language
        const footerText = FOOTER_MAP.get(options.branding)?.[options.language ?? 'nl'] ?? '';
        const disclaimerText = TRANSLATIONS['disclaimer_to_build']?.[options.language ?? 'nl'] ?? '';

        const pageFooter = this.convertTextToParagraph(
            footerText,
            this.pageWidth(options) - (options.margins.left ?? 0) - (options.margins.right ?? 0) - logoDimensions.width,
            {
                textStyle: {
                    fontSize: 9,
                    weight: FontWeight.THIN
                },
                margin: {
                    top: 0,
                    bottom: 0
                },
                horizontalTextAlignment: 'left'
            }, fonts);

        const disclaimer = this.convertTextToParagraph(
            disclaimerText,
            this.pageWidth(options) - (options.margins.left ?? 0) - (options.margins.right ?? 0) - logoDimensions.width,
            {
                textStyle: {
                    fontSize: 6,
                    weight: FontWeight.THIN
                },
                margin: {
                    top: 0,
                    bottom: 0
                },
                horizontalTextAlignment: 'left'
            }, fonts);

        // console.log('FOOTER', pageFooter, this.pageWidth(options) - options.margins.left - options.margins.right - logoDimensions.width);
        doc.getPages().forEach((page, index) => {
            // Show payoff on every page
            if (index !== 0 || options.branding !== Branding.TO_BUILD_PROJECTS) {
                this.drawSvg(page,
                    logoSvgPaths,
                    {
                        x: (options.margins.left ?? 0) + logoHorOffset,
                        y: (options.margins.bottom ?? 0) + logoDimensions.height
                    });
            }

            if (index === 0) {
                this.drawParagraphOnPage(page, pageFooter, {
                    x: (options.margins.left ?? 0) + logoDimensions.width,
                    y: (options.margins.bottom ?? 0) + 60
                }, fonts);
                this.drawParagraphOnPage(page, disclaimer, {
                    x: (options.margins.left ?? 0) + logoDimensions.width,
                    y: (options.margins.bottom ?? 0) + 33
                }, fonts);
            }
        });
    }

    private pageHeight(options: PdfExportOptions) {
        return options.paper.orientation === 'LANDSCAPE' ? PAPER_DIMENSIONS[options.paper.size].width : PAPER_DIMENSIONS[options.paper.size].height ?? 200;
    }

    private pageWidth(options: PdfExportOptions) {
        return options.paper.orientation === 'LANDSCAPE' ? PAPER_DIMENSIONS[options.paper.size].height : PAPER_DIMENSIONS[options.paper.size].width ?? 200;
    }

    private createCalculationPageHeader(meta: CalculationMetaApi, width: number, fonts: PdfExportParagraphFonts) {
        return this.convertTextToParagraph(`${meta.projectId} - ${meta.description}`, width, {
            border: {
                color: 0x000000,
                sides: CellSide.bottom,
                thickness: 1
            },
            textStyle: {
                fontSize: 10,
                weight: FontWeight.THIN
            },
            margin: {
                top: 0,
                bottom: 1.5
            },
            horizontalTextAlignment: 'center'
        }, fonts);
    }

    private drawCalculationTableHeader(page: PDFPage, position: Position, contents: PdfExportParagraph[], fonts: PdfExportParagraphFonts) {
        let { x, y } = position;
        let maxParagraphHeight = 0;

        contents.forEach((c, i) => {

            this.drawParagraphOnPage(
                page,
                c,
                { x, y },
                fonts
            )

            x += c.paragraphDimensions.width;
            maxParagraphHeight = Math.max(maxParagraphHeight, c.paragraphDimensions.height);
        });

        y -= maxParagraphHeight;

        // Draw bottom line
        page.drawLine({ start: { x: position.x, y }, end: { x, y }, color: this.convertColor(0xc0c0c0), thickness: 1 })
    }

    private calculationColumns(columns: ColumnProps[], availableWidth: number) {
        // Calculate horizontal space available for columns and distribute based on relative widths
        const totalRelWidth = columns.reduce((p, c) => p + c.relativeWidth, 0);
        const pointsPerUnit = availableWidth / totalRelWidth;

        // Map to column properties with calculated width in points
        const calculatedColumns = columns.map<ColumnProps>(column => ({
            ...column,
            calculatedWith: column.relativeWidth * pointsPerUnit
        }));

        return calculatedColumns;
    }

    private async addCalculationHeader(page: PDFPage, meta: CalculationMetaApi, options: PdfExportOptions, fonts: Map<string, PDFFont>) {

    }

    private formatDateToDutch(date: Date): string {
        // Maak een formatter aan voor de dag van de week, dag, maand en jaar
        const options: Intl.DateTimeFormatOptions = {
            weekday: 'long',  // "woensdag"
            day: 'numeric',   // "21"
            month: 'long',    // "augustus"
            year: 'numeric'   // "2024"
        };

        // Gebruik de 'nl-NL' locale om het in het Nederlands te formatteren
        const formatter = new Intl.DateTimeFormat('nl-NL', options);
        return formatter.format(date);
    }

    private convertColor(color: number): RGB {
        return rgb(
            ((color & 0xff0000) >> 16) / 0xff,
            ((color & 0xff00) >> 8) / 0xff,
            (color & 0xff) / 0xff
        )
    }

    private drawSvg(page: PDFPage, svg: SvgPath[], position: { x: number, y: number }) {
        const { x, y } = position;
        // Start path drawing
        svg.forEach(segment => {

            // page.drawCircle({ x, y, size: 2, borderColor: rgb(0, 0, 0) });
            page.drawSvgPath(segment.path, {
                borderColor: rgb(0, 0, 0),
                color: this.convertColor(segment.fillColor!),
                // borderWidth: 0.1,
                x: x + (segment.translation?.x ?? 0),
                y: y + (segment.translation?.y ?? 0),
                scale: (segment.scale) || 1
            })
        })

        // Marker
        // page.drawCircle({x, y, size: 100, borderColor: rgb(0, 0, 0)});
        // page.drawCircle({x, y, size: 20, borderColor: rgb(0, 0, 0)});
    }

    private measureTextWidth(text: string, style: TextStyle, fonts: PdfExportParagraphFonts) {
        if (!text) {
            return 0;
        }

        const font = fonts[styleToFont(style)];

        if (!font) {
            throw new Error('Font style not linked to PDF font');
        }

        if (!style.fontSize) {
            // console.log('INVALID STYLE', text, style);
        }

        return font.widthOfTextAtSize(text, style.fontSize);
    }

    /**
     * Function will convert input text to paragraph object
     * This object can then be rendered to a pdf page
     * Some HTML tags are supported in the input text:
     * <br /><p></p><b></b><li></li>
     * These will convert to formatted text
     *
     * @private
     * @param {string} htmlText Text with limited support of HTML-tags
     * @param {number} availableWidth The horizontal width available to render paragraph, height is calculated
     * @param {TextStyle} defaultTextStyle This is the style that every created text span will inherit
     * @param {CellFormat} format This is the paragraph style
     * @param {PdfExportParagraphFonts} fonts The font mapping for different styles
     * @return {*}  {Promise<PdfExportParagraph>}
     * @memberof PdfExportService
     */
    private convertTextToParagraph(
        htmlText: string,
        availableWidth: number,
        format: CellFormat,
        fonts: PdfExportParagraphFonts
    ): PdfExportParagraph {
        // First process any HTML tags in the text
        const paragraph = this.parseHtmlToPdfExport(htmlText, format, availableWidth, fonts, format.textStyle?.weight ?? FontWeight.REGULAR, format.textStyle?.boldWeight ?? FontWeight.MEDIUM);

        // console.log('CONVERTED PARAGRAPH', paragraph);

        // Fit to vertical size available
        return this.limitParagraphWidth(paragraph, availableWidth, fonts);
    }

    /**
     * Render a paragraph to a PDF page
     *
     * @private
     * @param {PDFPage} page The page to render to
     * @param {PdfExportParagraph} paragraph The paragraph being rendered
     * @param {Position} position The top left coordinates of the paragraph on the page. Y position is relative to page bottom
     * @param {Dimensions} dimensions The dimensions of the outer box of the paragraph
     * @param {PdfExportParagraphFonts} fonts The font mapping for different styles
     * @memberof PdfExportService
     */
    private drawParagraphOnPage(page: PDFPage, paragraph: PdfExportParagraph, position: Position, fonts: PdfExportParagraphFonts) {
        // this.paragraphHelper(page, paragraph, position, fonts);
        let { x, y } = position;

        let relY = y;
        let relX = x;

        const {
            paragraphDimensions: {
                width: paragraphWidth = 0,
                height: paragraphHeight = 0
            } = { width: 0, height: 0 },
            style: {
                margin: {
                    left: marginLeft = 0,
                    top: marginTop = 0,
                    right: marginRight = 0,
                    bottom: marginBottom = 0
                } = { left: 0, top: 0, right: 0, bottom: 0 },
                border: {
                    thickness: borderThickness = 0,
                    sides: borderSides = 0,
                    color: borderColor = 0
                } = { thickness: 0, sides: 0, color: 0 },
                backgroundColor,
                lineSpacing = 1
            }
        } = paragraph;

        // If background color is given, draw a rectangle behind cell
        if (backgroundColor !== undefined) {
            const horizontalSides = [CellSide.bottom, CellSide.top].map(side => borderSides & side).filter(f => !!f).length;
            const verticalSides = [CellSide.left, CellSide.right].map(side => borderSides & side).filter(f => !!f).length;
            const borderWidth = borderThickness * verticalSides; // Factor 0.5 because line center is paragraph edge
            const borderHeight = borderThickness * horizontalSides;

            page.drawRectangle({
                x: x + ((borderSides & CellSide.left) ? borderThickness : 0),
                y: y - paragraphHeight + ((borderSides & CellSide.bottom) ? borderThickness : 0),
                width: paragraphWidth - borderWidth,
                height: paragraphHeight - borderHeight,
                color: this.convertColor(backgroundColor)
            });
        }

        if (borderSides & CellSide.top) {
            page.drawLine({
                start: {
                    x,
                    y: y - (borderThickness / 2)
                },
                end: {
                    x: x + paragraphWidth,
                    y: y - (borderThickness / 2)
                },
                color: this.convertColor(borderColor),
                thickness: borderThickness
            })
        }

        if (borderSides & CellSide.bottom) {
            page.drawLine({
                start: {
                    x,
                    y: y - paragraphHeight + (borderThickness / 2)
                },
                end: {
                    x: x + paragraphWidth,
                    y: y - paragraphHeight + (borderThickness / 2)
                },
                color: this.convertColor(borderColor ?? 0),
                thickness: borderThickness
            })
        }

        if (borderSides & CellSide.left) {
            page.drawLine({
                start: {
                    x,
                    y: y - (borderThickness / 2)
                },
                end: {
                    x,
                    y: y - paragraphHeight + (borderThickness / 2)
                },
                color: this.convertColor(borderColor ?? 0),
                thickness: borderThickness
            })
        }

        if (borderSides & CellSide.right) {
            page.drawLine({
                start: {
                    x: x + paragraphWidth,
                    y: y - (borderThickness / 2)
                },
                end: {
                    x: x + paragraphWidth,
                    y: y - paragraphHeight + (borderThickness / 2)
                },
                color: this.convertColor(borderColor ?? 0),
                thickness: borderThickness
            })
        }

        // Vertical starting point is y position
        relY -= (borderSides & CellSide.top ? borderThickness : 0);

        // PDF coordinates system uses base line for positioning text
        // Adjusting so given position is handeled as top left of cell

        // Subtract line height, minus the fonts descent
        // Since the text is placed with baseline on y position
        relY -= this.heightWithoutDescent(paragraph.lines[0], fonts) ?? 0;

        // Subtract top margin
        relY -= marginTop ?? 0;

        paragraph.lines.forEach((line, i) => {
            switch (line.horizontalTextAlignment) {
                case 'center':
                    relX = x + (paragraphWidth - line.dimensions.width) / 2;
                    break;
                case 'right':
                    relX = x + paragraphWidth - line.dimensions.width - marginRight;
                    break;
                case 'left': // Add left margin
                default:
                    relX = x + marginLeft;
                    break;
            }

            // Bullet
            if (line.hasBullet) {
                const firstSpan = line.spans!.find(Boolean);

                page.drawText('-', {
                    x: relX + 4, // Hardcoded indentation 
                    y: relY,
                    size: firstSpan?.style.fontSize ?? 8,
                    font: fonts[styleToFont(firstSpan?.style ?? { fontSize: 8, weight: FontWeight.REGULAR })],
                    color: this.convertColor(firstSpan?.style.color ?? 0),
                });
            }

            if (line.hasBullet || line.indent) {
                relX += BULLET_WIDTH;
            }

            this.drawLineOnPage(page, paragraph, i, { x: relX, y: relY }, fonts);

            relY -= line.dimensions.height * lineSpacing;
        });

        // DEBUG: 
        // page.drawRectangle({
        //     x,
        //     y: y - paragraphHeight,
        //     width: paragraphWidth,
        //     height: paragraphHeight,
        //     borderColor: rgb(1, 0, 0),
        //     opacity: 0.5
        // });

        return paragraphHeight;
    }

    private drawLineOnPage(page: PDFPage, paragraph: PdfExportParagraph, lineNr: number, position: Position, fonts: PdfExportParagraphFonts) {
        let {
            x,
            y
        } = position;

        const line = paragraph.lines[lineNr];

        for (const span of line.spans ?? []) {
            page.drawText(span.text, {
                x,
                y,
                size: span.style.fontSize,
                font: fonts[styleToFont(span.style)],
                color: this.convertColor(span.style.color ?? 0),
            });

            if (span.style.isUnderlined) {
                page.drawLine({
                    start: { x, y: y - 1 },
                    end: { x: x + span.width, y: y - 1 },
                    color: this.convertColor(span.style.color ?? 0),
                    opacity: 0.8,
                    thickness: 0.4
                })
            }

            x += span.width;
        }
    }

    private paragraphHelper(page: PDFPage, paragraph: PdfExportParagraph, position: Position, fonts: PdfExportParagraphFonts) {

        // Debug: circle marking text position
        page.drawEllipse({
            x: position.x,
            y: position.y,
            xScale: 2,
            yScale: 2,
            borderColor: rgb(0, 0, 0)
        });

        // Draw line at paragraph position
        page.drawLine({
            start: {
                x: position.x + (paragraph.style.margin?.left ?? 0),
                y: position.y,
            },
            end: {
                x: position.x + (paragraph.style.margin?.left ?? 0) + paragraph.textDimensions.width,
                y: position.y
            },
            thickness: 1,
            color: rgb(0, 0, 1)
        });

        const height = this.heightWithoutDescent(paragraph.lines[0], fonts);

        page.drawLine({
            start: {
                x: position.x + (paragraph.style.margin?.left ?? 0),
                y: position.y - (height ?? 0),
            },
            end: {
                x: position.x + (paragraph.style.margin?.left ?? 0) + paragraph.textDimensions.width,
                y: position.y - (height ?? 0)
            },
            thickness: 1,
            color: rgb(0, 1, 1)
        })

        page.drawLine({
            start: {
                x: position.x + (paragraph.style.margin?.left ?? 0),
                y: position.y - paragraph.textDimensions.height - (paragraph.style.margin?.top ?? 0),
            },
            end: {
                x: position.x + (paragraph.style.margin?.top ?? 0) + paragraph.textDimensions.width,
                y: position.y - paragraph.textDimensions.height - (paragraph.style.margin?.top ?? 0),
            },
            thickness: 1,
            color: rgb(1, 0, 1)
        })
    }

    private heightWithoutDescent(line: PdfExportLine, fonts: PdfExportParagraphFonts) {
        return line ? line.spans?.reduce<number>((p, c) => Math.max(fonts[styleToFont(c.style)].heightAtSize(c.style.fontSize, { descender: false }), p), 0) : 0;
    }

    private parseHtmlToPdfExport(htmlText: string, format: CellFormat, width: number, fonts: PdfExportParagraphFonts, fontWeight: FontWeight, fontWeightBold?: FontWeight): PdfExportParagraph {
        // console.log('PARSE HTML TO PDF EXPORT', htmlText, format, width, fonts);
        let lines: PdfExportLine[] = [];
        let spans: PdfExportSpan[] = [];
        let lineIndex = 0;
        let currentText = '';
        let isBold = false;
        let isItalic = false;
        let isUnderlined = false;
        let hasBullet = false;

        const tokens = (htmlText ?? '')
            .replace(/[\n\r\t]/g, '')
            .replace(/\s\s+/g, ' ')
            .split(/(<\/?[^>]+>)/g) // Split on tags
            .filter(f => !!f && !f.match(/^\s+$/));      // Discard empty tokens

        // console.log('TOKENS', tokens);

        // Helper function to add current text as span to line
        const addCurrentTextToLine = () => {
            if ((currentText?.length ?? 0) > 0) {
                spans.push(this.createSpan(currentText, {
                    ...format.textStyle!,
                    weight: isBold ? fontWeightBold ?? fontWeight : fontWeight,
                    isItalic: isItalic,
                    isUnderlined: isUnderlined
                },
                    fonts
                ));

                // Reset text
                currentText = '';
            }
        }

        const createLineFromSpans = (newLine: boolean = false) => {
            // Add current text as span
            addCurrentTextToLine();

            // If there are spans, create a new line with them
            if (spans.length > 0) {
                // Fix for different handling of spaces in export and HTML interface
                // Start of first span
                spans[0].text = spans[0].text
                    .replace(/^\s+/gm, '');

                lines.push({ index: lineIndex++, spans, dimensions: this.calculateLineDimensions(spans), horizontalTextAlignment: format.horizontalTextAlignment, hasBullet });
                spans = [];
            } else if (newLine) {
                // console.log('NEW LINE', { dimensions: { height: format.textStyle.fontSize, width: 10 } })
                lines.push({
                    index: lineIndex++,
                    spans: [{
                        style: {
                            fontSize: format.textStyle!.fontSize ?? 8
                        }, text: '', width: 10
                    }],
                    dimensions: { height: format.textStyle!.fontSize, width: 10 },
                    horizontalTextAlignment: format.horizontalTextAlignment,
                    hasBullet
                });
            }

            hasBullet = false;
        }

        tokens.forEach(token => {
            switch (token) {
                case '<p>':
                case '<div>':
                case '</div>':
                    createLineFromSpans();
                    break;

                case '</li>':
                    createLineFromSpans();
                    break;

                case '</p>':
                case '<br>':
                case '<br />':
                case '<br/>':
                    createLineFromSpans(true);
                    break;

                case '<b>':
                    addCurrentTextToLine();
                    isBold = true;
                    break;

                case '</b>':
                    addCurrentTextToLine();
                    isBold = false;
                    break;

                case '<u>':
                    addCurrentTextToLine();
                    isUnderlined = true;
                    break;

                case '</u>':
                    addCurrentTextToLine();
                    isUnderlined = false;
                    break;

                case '<i>':
                    addCurrentTextToLine();
                    isItalic = true;
                    break;

                case '</i>':
                    addCurrentTextToLine();
                    isItalic = false;
                    break;

                case '<span>':
                case '</span>':
                    addCurrentTextToLine();
                    break;

                case '<li>':
                    // Can only occur at the start of the line
                    if (!currentText) {
                        hasBullet = true;
                    }

                    addCurrentTextToLine();
                    break;
                default:
                    // Check for dash used as bullet
                    if (!spans.length && /^\-+\s/.test(token)) {
                        // Add text without bullet
                        currentText += token.replace(/^\-+\s/, '');

                        hasBullet = true;
                    } else {
                        currentText += token;
                    }

                    break;
            }
        });

        // Create a new line from remaining text
        // If there is no line and no spans, create an empty new line
        createLineFromSpans(!lines.length && !spans.length);

        const result: PdfExportParagraph = {
            textDimensions: { width: 0, height: 0 },
            paragraphDimensions: { width: width, height: 0 },
            lines: lines,
            style: { ...format }
        }

        this.calculateParagraphDimensions(result);

        // console.log('HTML to PARAGRAPH', htmlText, result);

        return result;
    }

    private calculateLineDimensions(spans: PdfExportSpan[]) {
        return spans.reduce<Dimensions>((p, c) => {
            p.width += c.width;
            p.height = Math.max(c.style.fontSize, p.height)

            return p;
        }, { width: 0, height: 0 });
    }

    private calculateParagraphDimensions(paragraph: PdfExportParagraph) {
        const { width, height } = paragraph.lines.reduce((p, c, i) => {
            const width = Math.max(c.dimensions.width, p.width);
            const isLast = (i === (paragraph.lines.length - 1));
            const height = p.height + (c.spans ?? []).reduce((pS, cS) => Math.max(cS.style.fontSize * (isLast ? 1 : (paragraph.style.lineSpacing ?? 1)), pS), 0);

            return { width, height };
        }, { width: 0, height: 0 });

        const {
            style: {
                border: {
                    sides: borderSides = 0,
                    thickness: borderThickness = 0
                } = { sides: 0, thickness: 0 },
                margin: {
                    bottom: marginBottom = 0,
                    left: marginLeft = 0,
                    right: marginRight = 0,
                    top: marginTop = 0
                } = { bottom: 0, left: 0, right: 0, top: 0 }
            }
        } = paragraph

        // For paragraph height, add lines and margins
        const horizontalSides = [CellSide.bottom, CellSide.top].map(side => borderSides & side).filter(f => !!f).length;
        const verticalSides = [CellSide.left, CellSide.right].map(side => borderSides & side).filter(f => !!f).length;
        const paragraphWidth = width + marginLeft + marginRight + (borderThickness * verticalSides * 0.5); // Factor 0.5 because line center is paragraph edge
        const paragraphHeight = height + marginTop + marginBottom + (borderThickness * horizontalSides * 0.5);

        paragraph.textDimensions = { width, height };
        paragraph.paragraphDimensions = { width: paragraph.paragraphDimensions.width || paragraphWidth, height: paragraphHeight }


    }

    private createSpan(text: string, style: TextStyle, fonts: PdfExportParagraphFonts): PdfExportSpan {
        return {
            text: text,
            width: this.measureTextWidth(text, style, fonts),
            style
        };
    }

    /**
     * Make paragraph fit a given horizontal space
     * By splitting up lines that are too long
     *
     * @private
     * @param {PdfExportParagraph} paragraph
     * @param {number} paragraphWidth
     * @param {TextStyle} format.textStyle?
     * @param {PdfExportParagraphFonts} fonts
     * @return {*}  {PdfExportParagraph}
     * @memberof PdfExportService
     */
    private limitParagraphWidth(paragraph: PdfExportParagraph, paragraphWidth: number, fonts: PdfExportParagraphFonts): PdfExportParagraph {
        // Adjust lines based on available horizontal space
        const adjustedLines: PdfExportLine[] = [];

        const calculatedWidth = paragraphWidth - (paragraph.style.margin?.left ?? 0) - (paragraph.style.margin?.right ?? 0);

        for (const line of paragraph.lines) {
            // console.log('LINE', line.dimensions.width, calculatedWidth, line.spans.map(m => m.text).join(', '));
            if (line.dimensions.width > calculatedWidth) {
                // console.log('LINE TOO LONG', line.dimensions.width, calculatedWidth, line.spans.map(m => m.text).join(', '));
                adjustedLines.push(...this.splitUpLine(line, calculatedWidth, fonts))
            } else {
                adjustedLines.push(line);
            }
        }

        const adjustedParagraph: PdfExportParagraph = {
            ...paragraph,
            lines: adjustedLines
        }

        this.calculateParagraphDimensions(adjustedParagraph);

        return adjustedParagraph;
    }

    /**
     * Splits up a line in multiple lines
     * Based on a maximum length
     *
     * @private
     * @param {PdfExportLine} line The line to split up
     * @param {number} maxWidth The available horizontal space
     * @param {PdfExportParagraphFonts} fonts A map with fonts for different styles
     * @return {*}  {PdfExportLine[]}
     * @memberof PdfExportService
     */
    private splitUpLine(line: PdfExportLine, maxWidth: number, fonts: PdfExportParagraphFonts): PdfExportLine[] {
        // Cap max width
        maxWidth = Math.max(maxWidth, 20);

        const result: PdfExportLine[] = [];
        let curLine: PdfExportLine;
        let curIndex = 0;
        let spansLeft: PdfExportSpan[] = [...line.spans ?? []];
        let spaceLeft = line.hasBullet ? maxWidth - BULLET_WIDTH : maxWidth;

        // Helper function to reset the curruent line to default properties
        const resetCurLine = () => {
            curLine = {
                index: curIndex++,
                dimensions: { width: 0, height: 0 },
                spans: [],
                horizontalTextAlignment: line.horizontalTextAlignment,
                hasBullet: line.hasBullet && !result.length,
                indent: line.hasBullet && !!result.length
            }
        }

        // Start with empty line
        resetCurLine();

        // Helper functions to split up a span based on the space left

        const finalizeCurrentLine = () => {
            curLine.dimensions = this.calculateLineDimensions(curLine.spans ?? []);
            result.push(curLine);

            resetCurLine();
        }

        const addAllWords = () => {
            // console.log('ALL WORDS FIT');
            // Everything fits
            curLine!.spans!.push(spansLeft?.shift()!);
            curLine.dimensions = this.calculateLineDimensions(curLine.spans ?? []);
            result.push(curLine);

            // Result line
            resetCurLine();
        }

        const addSomeWords = (span: PdfExportSpan, spanWords: string[], fittingWordsIndex: number) => {
            // console.log('SOME WORDS FIT');
            const addTextToSpan = spanWords.splice(0, fittingWordsIndex).join(' ');

            curLine!.spans!.push({
                ...span,
                text: addTextToSpan,
                width: this.measureTextWidth(addTextToSpan, span.style, fonts)
            });

            finalizeCurrentLine();

            span.text = spanWords.join(' ');
            span.width = this.measureTextWidth(span.text, span.style, fonts);
        }

        // Helper functions to add spans to the line

        const addAllSpans = () => {
            // All spans fit
            const useAllSpans = spansLeft.splice(0);
            curLine!.spans!.push(...useAllSpans);

            finalizeCurrentLine();
        }

        const addSomeSpans = (uptoIndex: number) => {
            // console.log('SOME FIT');
            const selectedSpans = spansLeft.splice(0, uptoIndex);
            curLine!.spans!.push(...selectedSpans);
            curLine!.dimensions = this.calculateLineDimensions(curLine!.spans!);
        }

        const splitUpNextSpan = () => {
            // The next span needs to be split up, since it does not fit the available width
            const splitUpSpan = spansLeft[0];
            const spanSpaceWidth = this.measureTextWidth(' ', splitUpSpan.style, fonts);

            spaceLeft = maxWidth - this.calculateLineDimensions(curLine!.spans!).width;

            // Try to fit part of the next span
            const spanWords = splitUpSpan.text.split(' ');

            let fittingWordsIndex = spanWords.findIndex(i => {
                spaceLeft -= (this.measureTextWidth(i, splitUpSpan.style, fonts) + spanSpaceWidth);
                return spaceLeft <= 0;
            });

            if (fittingWordsIndex < 0) {
                // All words fit, so add them all
                addAllWords();
            } else if (fittingWordsIndex === 0 && curLine!.spans!.length > 0) {
                finalizeCurrentLine();
            } else {
                addSomeWords(splitUpSpan, spanWords, Math.max(1, fittingWordsIndex));   // Always add the first word, even if it does not fit
            }
        }

        while (spansLeft.length > 0) {
            // Take all spans that fit the maximum line width
            // Return the index of the first span that does not fit
            const uptoIndex = spansLeft.findIndex(i => { spaceLeft -= i.width; return spaceLeft <= 0 });

            // If index returned equals -1, all remaining spans fit
            if (uptoIndex < 0) {
                addAllSpans();
            } else if (uptoIndex === 0) {
                splitUpNextSpan();
            } else {
                // Span index is larger than 0, so some of the spans will fit the space left
                addSomeSpans(uptoIndex);
            }
        }

        return result;
    }
}
